import React, { useEffect } from 'react';
import { Box, Button } from '@mui/material';
import { useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import {
  ModuleRegistry,
  AllCommunityModule,
} from 'ag-grid-community';
import { useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import styles from './PerformanceIndicators.module.scss';
import { getLatestReport } from 'store/performanceIndicators/thunk';
import { useAppDispatch } from 'store/hooks';
import { selectLatestPerformanceIndicatorsReport } from 'store/performanceIndicators/selectors';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './index.css';

ModuleRegistry.registerModules([
  AllCommunityModule,
]);

const COLORED_CELL_CLASS = ['relative_performance_flag'];
const PERCENTAGE_CELL_CLASS = ['ratio_of_branch_frontend_bookings'];
const DATE_CELL_CLASS = ['last_week', 'last_week_prev_year'];
const BOOLEAN_CELL_CLASS = ['ltb_status', 'status_50_pct_rule'];
const AGGREGATE_CELL_CLASS = ['problems_count'];

const GREEN_COLOR = '#90df90';
const YELLOW_COLOR = '#dfdf90';
const ORANGE_COLOR = '#dfa990';
const RED_COLOR = '#df9090';
const DARK_RED_COLOR = '#af3030';

const CELL_RENDER_STYLE = {
  COLORED_CELL: 'colored-cell',
  PERCENTAGE_CELL: 'percentage-cell',
  DATE_CELL: 'date-cell',
  BOOLEAN_CELL: 'boolean-cell',
  AGGREGATE_CELL: 'aggregate-cell',
  DEFAULT_CELL: 'default-cell',
}

export function PerformanceIndicators() {
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const latestPerformanceIndicatorsReport = useSelector(selectLatestPerformanceIndicatorsReport);

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    dispatch(getLatestReport());
  }, [dispatch]);

  const columns = latestPerformanceIndicatorsReport?.columns || [];
  const data = latestPerformanceIndicatorsReport?.data || [];

  const filteredData = data.filter((row) => {
    for (const [key, value] of Array.from(searchParams.entries())) {
      if (row[columns.indexOf(key)]?.toString() !== value?.toString()) {
        return false;
      }
    }

    return true;
  })

  const rowData = filteredData.map((row) => {
    return columns.reduce((acc, column, index) => {
      acc[column] = row[index];
      return acc;
    }, {});
  }, []);

  const filtarableColumns = ['region', 'store_id', 'store_name'];

  const colorCellRenderer = (intl, params, label) => {
    const colors = {
      '-1': RED_COLOR,
      '0': YELLOW_COLOR,
      '1': GREEN_COLOR,
    }
    return <div style={{backgroundColor: colors[params.value.toString()], textAlign: 'center'}}>{label}</div>;
  }

  const getCellType = (params) => {
    const { field } = params.colDef;
  
    if (COLORED_CELL_CLASS.includes(field)) {
      return CELL_RENDER_STYLE.COLORED_CELL;
    }

    if (PERCENTAGE_CELL_CLASS.includes(field)) {
      return CELL_RENDER_STYLE.PERCENTAGE_CELL;
    }

    if (DATE_CELL_CLASS.includes(field)) {
      return CELL_RENDER_STYLE.DATE_CELL;
    }

    if (BOOLEAN_CELL_CLASS.includes(field)) {
      return CELL_RENDER_STYLE.BOOLEAN_CELL;
    }

    if (AGGREGATE_CELL_CLASS.includes(field)) {
      return CELL_RENDER_STYLE.AGGREGATE_CELL;
    }

    return CELL_RENDER_STYLE.DEFAULT_CELL;
  }

  const getCellValue = (params, cellType, field = '') => {
    switch (cellType) {
      case CELL_RENDER_STYLE.COLORED_CELL:
        return intl.formatMessage({ id: `performanceIndicatorsColumnName.relative_performance_flag_${params.value}` });
      case CELL_RENDER_STYLE.PERCENTAGE_CELL:
        return `${(100 * params.value).toFixed(0)} %`;
      case CELL_RENDER_STYLE.DATE_CELL:
        return dayjs(params.value).format('DD.MM.YYYY');
      case CELL_RENDER_STYLE.BOOLEAN_CELL:
        return params.value ? intl.formatMessage({ id: 'common.yes' }) : intl.formatMessage({ id: 'common.no' });
      case CELL_RENDER_STYLE.AGGREGATE_CELL:
        return params.value;
      default:
        const canValueBeConvertedtoString = params.value !== null && (parseInt(params.value, 10).toString() === params.value.toString())
        return canValueBeConvertedtoString ?
          intl.formatMessage({ id: `performanceIndicatorsColumnName.${field}_${params.value}`}) :
          (params.value === null ? '-' : params.value);
    }
  }
  
  const getCellRendered = (intl, params) => {
    const { field } = params.colDef;

    const cellType = getCellType(params);

    switch (cellType) {
      case CELL_RENDER_STYLE.COLORED_CELL:
        return colorCellRenderer(intl, params, getCellValue(params, cellType));
      case CELL_RENDER_STYLE.DATE_CELL:
          return <>{dayjs(params.value).format('DD.MM.YYYY')}</>;
      case CELL_RENDER_STYLE.PERCENTAGE_CELL:
      case CELL_RENDER_STYLE.BOOLEAN_CELL:
        return <>{getCellValue(params, cellType)}</>;
      case CELL_RENDER_STYLE.AGGREGATE_CELL:
        let color = GREEN_COLOR;
        if (params.value > 0) {
          if (params.value < 3) {
            color = YELLOW_COLOR;
          } else if (params.value < 5) {
            color = ORANGE_COLOR;
          } else {
            color = RED_COLOR;
          }
        }
        return <div style={{backgroundColor: color, textAlign: 'center'}}>{params.value}</div>;
      default:
        const canValueBeConvertedtoString = params.value !== null && (parseInt(params.value, 10).toString() === params.value.toString())
        return <div style={{color: canValueBeConvertedtoString ? DARK_RED_COLOR: 'inherit'}}>{getCellValue(params, cellType, field)}</div>;
    }
  }

  const columnDefs = columns.map((column) => ({
    field: column,
    sort: column === 'storeid' ? 'asc' : undefined,
    sortIndex: column === 'storeid' ? 0 : undefined,
    // filter: filtarableColumns.includes(column) ? 'agSetColumnFilter' : undefined,
    floatingFilter: false,
    headerName: intl.formatMessage({ id: `performanceIndicatorsColumnName.${column}` }),
    headerTooltip: intl.formatMessage({ id: `performanceIndicatorsColumnName.${column}` }),
    resizable: true,
    cellRenderer: (params) => getCellRendered(intl, params),
    tooltipValueGetter: (params) => { 
      const cellType = getCellType(params);
      const cellValue = getCellValue(params, cellType, params.colDef.field);
      return `${intl.formatMessage({ id: `performanceIndicatorsColumnName.${column}` })}: ${cellValue}`
    },
  }));

  const rowClassRules = {
    'row-odd': (params) => params.node.rowIndex % 2 === 0, // Odd rows
    'row-even': (params) => params.node.rowIndex % 2 !== 0, // Even rows
  };

  const onCellDoubleClicked = (params: any) => {
    if (params.value === null) {
      return;
    }
    const previousSearchParameters = searchParams;

    if (previousSearchParameters.get(params.colDef.field)) {
      previousSearchParameters.delete(params.colDef.field);
    } else {
      previousSearchParameters.set(params.colDef.field, params.value);
    }

    setSearchParams(previousSearchParameters);
  };

  return (
    <Box className={styles.container}>
      <Box component="section" sx={{ borderRadius: 1, marginLeft: 5, marginRight: 5 }}>
        <h1>
          Performance Indicators - {data.length} stores {data.length !== filteredData.length ? `(showing ${filteredData.length} stores)`: ''}
          {data.length !== filteredData.length && (<Button onClick={() => setSearchParams('')} variant="contained" className={styles.clearFilters}>
            {intl.formatMessage({ id: 'common.clearFilters' })}
            </Button>)}
        </h1>
        <div className={styles.agGridTable}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            rowClassRules={rowClassRules}
            onCellDoubleClicked={onCellDoubleClicked}
            headerHeight={60}
            tooltipShowDelay={20}
            tooltipMouseTrack={true}
            tooltipHideDelay={5000}
            icons={{
              filter: '<img src="img/Filter.svg" style="width: 17px; height: 17px;" />',
            }}
          />
        </div>
      </Box>
    </Box>
  );
}
